<i18n locale="en">
{
  "content": "Description",
  "youtube": "YouTube Link",
  "heading": "Heading",
  "source": "Video source",
  "sources": {
    "youtube": "YouTube",
    "cdn": "Upload"
  }
}
</i18n>

<template>
  <div class="flex-col gap-8">
    <OwnInputContainer :label="t('source')">
      <OwnSegmentedControl
        :options="sourceOptions"
        :model-value="selectedSource"
        @update:model-value="changeSource"
      />
    </OwnInputContainer>
    <OwnInputContainer
      class="video-editor__input-container"
      :label="t('heading')"
      :errors="header.status.errors"
    >
      <OwnInput v-model="header.value" :placeholder="t('heading')" />
    </OwnInputContainer>
    <OwnInputContainer
      v-if="selectedSource === 'youtube'"
      class="video-editor__input-container"
      :label="t('youtube')"
      :errors="videoId.status.errors"
    >
      <YoutubeInput v-model="videoId.value" />
    </OwnInputContainer>
    <OwnInputContainer v-else :label="t('sources.cdn')">
      <HostedVideoInput
        :brand-id="currentBlock.brandId"
        :model-value="
          currentBlockParams.video.type === 'cdn'
            ? currentBlockParams.video.params
            : null
        "
        :on-video-ready="applyHostedVideoUpdate"
        :upload-key="id"
      />
    </OwnInputContainer>
    <OwnInputContainer
      class="video-editor__input-container"
      :label="t('content')"
      :errors="body.status.errors"
    >
      <OwnTextarea v-model="body.value" :placeholder="t('content')" />
    </OwnInputContainer>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { mapGetters } from 'vuex'

import YoutubeInput from '@/components/inputs/YoutubeInput'
import { stopHostedVideoPolling } from '@/features/website-builder/builder/components/hostedVideoPolling'
import { WebsiteSectionValidators } from '@/features/website-builder/builder/validation/websiteSectionValidators'
import { FormBuilder } from '@/forms'
import { OwnInput } from '@/ui/OwnInput'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { OwnSegmentedControl } from '@/ui/OwnSegmentedControl'
import { OwnTextarea } from '@/ui/OwnTextarea'

import HostedVideoInput from '../../../components/HostedVideoInput.vue'
import { VideoCommands } from '../commands/VideoCommands'

export default {
  name: 'VideoEditor',
  components: {
    HostedVideoInput,
    OwnInput,
    OwnInputContainer,
    OwnSegmentedControl,
    OwnTextarea,
    YoutubeInput,
  },
  mixins: [
    FormBuilder({
      body: {
        debounce: 250,
        validateOnInit: true,
        validators: [WebsiteSectionValidators.required],
      },
      header: {
        debounce: 250,
        validateOnInit: true,
        validators: [WebsiteSectionValidators.required],
      },
      videoId: {
        debounce: 250,
        validateOnInit: true,
        validators: [WebsiteSectionValidators.required],
      },
    }),
  ],
  props: {
    /**
     * Id getting passed to the editor component
     */
    id: { type: String, required: true },
  },
  setup(props) {
    const { t } = useI18n()
    const applyHostedVideoUpdate = async (update) => {
      await VideoCommands.updateBlock({
        targetId: props.id,
        update: { params: { video: { params: update, type: 'cdn' } } },
      })
    }

    return { applyHostedVideoUpdate, t }
  },
  data() {
    return {
      selectedSource: 'youtube',
      sourceOptions: ['youtube', 'cdn'].map((source) => ({
        label: this.t(`sources.${source}`),
        value: source,
      })),
    }
  },
  computed: {
    ...mapGetters({
      blockById: 'websiteBuilder/blockById',
    }),
    currentBlock() {
      return this.blockById(this.id)
    },
    currentBlockParams() {
      return this.currentBlock?.params
    },
  },
  created() {
    const { currentBlockParams } = this

    this.selectedSource = currentBlockParams.video.type
    this.setInitialFormValues({
      body: currentBlockParams.body,
      header: currentBlockParams.header,
      videoId:
        currentBlockParams.video.type === 'youtube'
          ? currentBlockParams.video.params.id
          : 'hosted',
    })
  },
  methods: {
    async applyBodyUpdate(update) {
      const { id } = this

      await VideoCommands.updateBlock({
        targetId: id,
        update: { params: { body: update } },
      })
    },
    async applyHeaderUpdate(update) {
      const { id } = this

      await VideoCommands.updateBlock({
        targetId: id,
        update: { params: { header: update } },
      })
    },
    async applyVideoIdUpdate(update) {
      const { id } = this

      await VideoCommands.updateBlock({
        targetId: id,
        update: { params: { video: { params: { id: update } } } },
      })
    },
    async changeSource(source) {
      this.selectedSource = source
      if (source !== 'youtube') {
        this.videoId.value = 'hosted'
        return
      }

      await stopHostedVideoPolling(this.id)
      this.videoId.value = ''
      await VideoCommands.updateBlock({
        targetId: this.id,
        update: {
          params: { video: { params: { id: '' }, type: 'youtube' } },
        },
      })
    },
    onFormControlUpdate(controlName, update) {
      const { isFormValid } = this

      if (!isFormValid) {
        return
      }

      switch (controlName) {
        case 'header':
          this.applyHeaderUpdate(update)
          break
        case 'videoId':
          this.applyVideoIdUpdate(update)
          break
        case 'body':
          this.applyBodyUpdate(update)
          break
      }
    },
  },
}
</script>
